function arr(v: any): any[] {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === 'object') return Object.values(v).flat().filter(Boolean);
  return [v];
}

function pick(o: any, keys: string[]) {
  for (const k of keys) {
    const v = o?.[k];
    if (v) return v;
  }
  return null;
}

function riskLabel(item: any) {
  const severity = `${item?.severity ?? item?.risk ?? ''}`.toLowerCase();
  if (severity.includes('high') || severity.includes('block')) return 'High';
  if (severity.includes('medium') || severity.includes('warning')) return 'Medium';
  return 'Low';
}

export default function GovernorRepairSuggestions({ runtime }: { runtime: any }) {
  try {
    const repairs = arr(
      pick(runtime, [
        'autoRepairQueueOutput',
        'autoRepairQueue',
        'repairQueue',
        'repairs',
        'autonomousRepairSuggestions',
        'repairSuggestions',
      ])
    );

    const findings = arr(pick(runtime, ['findings', 'report']));
    const fallback = findings
      .filter((x: any) => {
        const s = `${x?.status ?? x?.severity ?? x?.verdict ?? ''}`.toLowerCase();
        return s.includes('block') || s.includes('warning') || s.includes('degraded') || s.includes('open');
      })
      .map((x: any) => ({
        ...x,
        title: x?.title || x?.issue || 'Governor finding needs repair review',
        recommendedAction: x?.recommendedAction || x?.suggestedNextAction || 'Review this finding and route it through the repair engine.',
      }));

    const rows = repairs.length ? repairs : fallback;

    return (
      <section className="rounded-[2rem] bg-white p-6 mb-6 shadow-sm">
        <h2 className="text-4xl mb-3">Autonomous Constitution Repair Suggestions</h2>
        <p className="text-slate-600 mb-4">
          Suggested repairs are displayed safely. No repair is executed automatically from this panel.
        </p>

        {rows.length === 0 ? (
          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-slate-700">
            No autonomous repair suggestions are currently available.
          </div>
        ) : (
          <div className="space-y-4">
            {rows.slice(0, 20).map((item: any, i: number) => (
              <div key={item.id || item.repairId || item.runtimeEvidenceId || i} className="rounded-2xl bg-amber-50 border border-amber-200 p-4">
                <div className="font-bold text-lg">{item.title || item.name || `Repair suggestion ${i + 1}`}</div>
                <div className="text-sm text-slate-700 mt-2"><b>Domain:</b> {item.domain || item.subsystem || 'governor'}</div>
                <div className="text-sm text-slate-700"><b>Rule applied:</b> {item.ruleApplied || item.constitutionRuleApplied || 'KinsCircle Constitution repair review'}</div>
                <div className="text-sm text-slate-700"><b>Root cause:</b> {item.reason || item.summary || item.issue || 'No root-cause text supplied yet.'}</div>
                <div className="text-sm text-slate-700"><b>Auto repair available:</b> {item.autoRepairConsidered === false ? 'No' : 'Review required'}</div>
                <div className="text-sm text-slate-700"><b>Risk:</b> {riskLabel(item)}</div>
                <div className="text-sm text-slate-700"><b>Confidence:</b> {item.confidence ?? item.score ?? 'pending'}{typeof (item.confidence ?? item.score) === 'number' ? '%' : ''}</div>
                <div className="text-sm text-slate-700 mt-2"><b>Next action:</b> {item.recommendedAction || item.suggestedNextAction || 'Verify evidence, then route through Governor repair engine.'}</div>
                {item.runtimeEvidenceId ? (
                  <div className="text-xs text-slate-500 mt-2">Evidence: {item.runtimeEvidenceId}</div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>
    );
  } catch (e: any) {
    return (
      <section className="rounded-[2rem] bg-white p-6 mb-6 shadow-sm border border-red-200">
        <h2 className="text-3xl mb-3 text-red-900">Repair Suggestions panel failed</h2>
        <p className="text-slate-700">The Command Center stayed online.</p>
        <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-900 break-words">
          {e?.message || String(e)}
        </div>
      </section>
    );
  }
}
